import { weekFileName } from '../lib/weekFileName';
import { DocLangToggle } from './ui/doc-lang-toggle';
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Plus, AlertTriangle, MapPin, Copy, Download, Printer, Camera, FileSignature, Trash2, Lock, X } from 'lucide-react';
import EntityPhotoGallery from './EntityPhotoGallery';
import DamageReportRepairTasks from './DamageReportRepairTasks';
import { SearchSortBar, CREATED_SORT_OPTIONS, matchesQuery, matchesWeek, byCreated, byText } from './ListSearchSort';
import { useSavedView } from '../hooks/useSavedView';
import { FloatingSaveButton } from './FloatingSaveButton';
import { loadDocs, createDoc, saveDoc, signDoc, isLocalId } from '../lib/offlineDocs';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import useDeepShortcut from '../hooks/useDeepShortcut';
import { OfflineNotice, PendingSyncBadge } from './OfflineNotice';
import usePermissions from '../hooks/usePermissions';
import { dateLocale } from '../lib/dateFormat';

const DEFAULT_SIGN_OFF_ROLES = ['Reported By', 'Approved By', 'Customer Representative'];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_OPTIONS = ['Minor', 'Moderate', 'Severe', 'Critical'];
const SEVERITY_COLORS = {
  Minor:    'bg-gray-100 text-gray-700 border-gray-200',
  Moderate: 'bg-amber-50 text-amber-700 border-amber-200',
  Severe:   'bg-orange-50 text-orange-700 border-orange-200',
  Critical: 'bg-red-50 text-red-700 border-red-200',
};
const REPAIR_STATUSES = ['Reported', 'In Repair', 'Repaired', 'Written Off'];
const STATUS_COLORS = {
  draft:      'bg-gray-100 text-gray-700',
  signed_off: 'bg-green-100 text-green-700',
};

const emptyForm = {
  title: '', description: '', location: '',
  incident_date: '', incident_time: '',
  severity: 'Minor', affected_party: '', reported_by: '',
  estimated_repair_hours: '', estimated_cost: '', cost_currency: 'PLN',
  replacement_part_number: '', repair_status: 'Reported', repair_notes: '',
};

const ProjectDamageReports = ({ projectId, projectName, user }) => {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const isManager = user?.role === 'admin' || user?.role === 'manager';
  const token = localStorage.getItem('token');
  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [reports, setReports] = useState([]);
  const [affectedOptions, setAffectedOptions] = useState([
    'Equipment', 'Part', 'Cabinet', 'Expertfix', 'Customer', 'Third party'
  ]);
  const [loading, setLoading] = useState(false);
  const [filterLocation, setFilterLocation] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterAffected, setFilterAffected] = useState('');
  const [filterRepair, setFilterRepair] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const { search, setSearch, sortBy, setSortBy } = useSavedView('project-damage');
  const [week, setWeek] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);    // existing report or null for new
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [offlineList, setOfflineList] = useState(false);
  const isOnline = useOnlineStatus();

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const { rows, offline } = await loadDocs('damage_report', projectId, async () => (
        (await axios.get(`${API}/project-damage-reports?project_id=${projectId}`, authHeader)).data
      ));
      setReports(rows);
      setOfflineList(offline);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load damage reports');
    } finally {
      setLoading(false);
    }
  }, [projectId, authHeader]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  // Pull the "Affected" dropdown options from the shared configurable
  // list (inventory_damage_affected — same list management used by
  // Inventory damage severities / repair statuses).
  useEffect(() => {
    axios.get(`${API}/inventory/config/damage_affected`, authHeader)
      .then((r) => {
        const names = (r.data || []).map((x) => x.name).filter(Boolean);
        if (names.length) setAffectedOptions(names);
      })
      .catch(() => { /* fall back to defaults; user might not have inventory module enabled */ });
  }, [authHeader]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, reported_by: user?.full_name || user?.username || '' });
    setDialogOpen(true);
  };
  // Dashboard shortcuts: pin the open report, or the New damage report form.
  useDeepShortcut({
    active: dialogOpen && !!editing,
    tab: 'projects',
    context: { openProjectId: projectId, projectTab: 'damage-reports', focusDamageId: editing?.id },
    label: editing?.report_number || editing?.title,
    subtitle: projectName || '',
    icon: 'damage',
    perms: ['projects.view'],
    priority: 3,
    focusKey: 'focusDamageId',
    onFocus: (id) => {
      const found = reports.find((r) => r.id === id);
      if (found) openEdit(found);
    },
  });
  useDeepShortcut({
    active: dialogOpen && !editing,
    tab: 'projects',
    context: { openProjectId: projectId, projectTab: 'damage-reports', create: 'damage_report' },
    label: t('auto.lbl.newDamageReport'),
    subtitle: projectName || '',
    icon: 'damage',
    perms: ['projects.view'],
    priority: 4,
    focusKey: 'create',
    onFocus: (v) => { if (v === 'damage_report') openNew(); },
  });

  const openEdit = async (r) => {    // Re-fetch single report so we get linked_tasks summary with hours/cost
    let full = r;
    if (isOnline && !isLocalId(r.id)) {
      try {
        const fresh = await axios.get(`${API}/project-damage-reports/${r.id}`, authHeader);
        full = fresh.data;
      } catch (_e) { /* fallback to list payload */ }
    }
    setEditing(full);
    setForm({
      title: full.title || '', description: full.description || '', location: full.location || '',
      incident_date: full.incident_date || '', incident_time: full.incident_time || '',
      severity: full.severity || 'Minor',
      affected_party: full.affected_party || '',
      reported_by: full.reported_by || '',
      estimated_repair_hours: full.estimated_repair_hours ?? '',
      estimated_cost: full.estimated_cost ?? '',
      cost_currency: full.cost_currency || 'PLN',
      replacement_part_number: full.replacement_part_number || '',
      repair_status: full.repair_status || 'Reported',
      repair_notes: full.repair_notes || '',
    });
    setDialogOpen(true);
  };

  const saveForm = async () => {
    setSaving(true);
    try {
      const body = {
        ...form,
        estimated_repair_hours: form.estimated_repair_hours === '' ? null : parseFloat(form.estimated_repair_hours),
        estimated_cost: form.estimated_cost === '' ? null : parseFloat(form.estimated_cost),
      };
      if (editing) {
        const { online } = await saveDoc({
          kind: 'damage_report',
          id: editing.id,
          body,
          doc: editing,
          label: `Damage report ${editing.report_number || ''}`.trim(),
          token,
          onlineSave: async () => (
            await axios.put(`${API}/project-damage-reports/${editing.id}`, body, authHeader)
          ).data,
        });
        toast.success(online
          ? 'Damage report updated'
          : 'Saved on this device — it will sync when you\'re back online');
      } else {
        const { online } = await createDoc({
          kind: 'damage_report',
          projectId,
          payload: { ...body, project_id: projectId },
          docShell: {
            project_name: projectName,
            linked_task_ids: [],
            sign_offs: DEFAULT_SIGN_OFF_ROLES.map((role) => ({
              role, name: null, signed: false, signed_at: null,
              signed_by_user_id: null, signature_method: null, signature_ref: null,
            })),
          },
          label: `Damage report — ${body.title || 'new'}`,
          token,
          onlineCreate: async () => (
            await axios.post(`${API}/project-damage-reports`, { ...body, project_id: projectId }, authHeader)
          ).data,
        });
        toast.success(online
          ? 'Damage report created'
          : 'Report started on this device — it will upload when you\'re back online');
      }
      setDialogOpen(false);
      fetchReports();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteReport = async (r) => {
    if (!window.confirm(`Delete damage report ${r.report_number}?`)) return;
    try {
      await axios.delete(`${API}/project-damage-reports/${r.id}`, authHeader);
      toast.success(t('auto.misc.deleted'));
      fetchReports();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const copyReport = async (r) => {
    try {
      const resp = await axios.post(`${API}/project-damage-reports/${r.id}/copy`, {}, authHeader);
      toast.success(`Copied as ${resp.data.report_number}`);
      fetchReports();
      openEdit(resp.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Copy failed'); }
  };

  const exportPdf = async (r, mono = false, withPhotos = true) => {
    try {
      const resp = await fetch(`${API}/project-damage-reports/${r.id}/pdf?mono=${mono}&with_photos=${withPhotos}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('PDF failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = weekFileName(`${r.report_number}${mono ? '_BW' : ''}${withPhotos ? '' : '_no_photos'}.pdf`); a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  // --- Inline sign-off (no separate dialog) ---
  const signRole = async (role, name) => {
    if (!editing) return;
    if (!name || !name.trim()) {
      toast.error('Enter the signer\'s name first');
      return;
    }
    const body = { role, name: name.trim(), signature_method: 'manual' };
    const signedLocally = {
      ...editing,
      sign_offs: (editing.sign_offs || []).map((so) => (
        so.role === role
          ? { ...so, name: name.trim(), signed: true, signed_at: new Date().toISOString(), pending_sync: true }
          : so
      )),
    };
    try {
      const { online, data } = await signDoc({
        kind: 'damage_report',
        id: editing.id,
        roleKey: role,
        body,
        doc: signedLocally,
        label: `Sign-off (${role}) — ${editing.report_number || 'damage report'}`,
        token,
        onlineSign: async () => (
          await axios.post(`${API}/project-damage-reports/${editing.id}/sign-off`, body, authHeader)
        ).data,
      });
      if (online) {
        toast.success(`Signed: ${role}`);
        setEditing(data);
      } else {
        setEditing(signedLocally);
        toast.success('Signed on this device — it will upload when you\'re back online');
      }
      fetchReports();
    } catch (e) { toast.error(e.response?.data?.detail || 'Sign-off failed'); }
  };

  const finaliseReport = async () => {
    if (!editing) return;
    if (!window.confirm('Lock this report without all sign-offs?\nPending roles will stay unsigned in the record (e.g. customer rep absent).')) return;
    try {
      const r = await axios.post(`${API}/project-damage-reports/${editing.id}/finalise`, {}, authHeader);
      toast.success(t('auto.misc.reportLocked'));
      setEditing(r.data);
      fetchReports();
    } catch (e) { toast.error(e.response?.data?.detail || 'Finalise failed'); }
  };

  // --- Derived ---
  const locationOptions = useMemo(() => {
    const s = new Set();
    for (const r of reports) if (r.location) s.add(r.location);
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [reports]);

  const SEVERITY_RANK = { Critical: 4, Severe: 3, Moderate: 2, Minor: 1 };
  const DAMAGE_SORTS = {
    created_desc: byCreated('desc'),
    created_asc: byCreated('asc'),
    number: byText((r) => r.report_number),
    severity: (a, b) => (SEVERITY_RANK[b.severity] || 0) - (SEVERITY_RANK[a.severity] || 0),
    incident_date: (a, b) => String(b.incident_date || '').localeCompare(String(a.incident_date || '')),
    repair_status: byText((r) => r.repair_status),
  };
  const filtered = useMemo(() => {
    const list = reports.filter((r) => {
      if (filterLocation && (r.location || '').toLowerCase() !== filterLocation.toLowerCase()) return false;
      if (filterSeverity && r.severity !== filterSeverity) return false;
      if (filterAffected && r.affected_party !== filterAffected) return false;
      if (filterRepair && r.repair_status !== filterRepair) return false;
      const d = r.incident_date || (r.created_at ? String(r.created_at).slice(0, 10) : '');
      if (filterDateFrom && d && d < filterDateFrom) return false;
      if (filterDateTo && d && d > filterDateTo) return false;
      if (!matchesWeek(week, d)) return false;
      return matchesQuery(search, [
        r.report_number, r.title, r.description, r.location, r.severity,
        r.affected_party, r.reported_by, r.repair_status, r.repair_notes,
        r.replacement_part_number, r.incident_date, r.created_by_name,
      ]);
    });
    return [...list].sort(DAMAGE_SORTS[sortBy] || DAMAGE_SORTS.created_desc);
    /* eslint-disable-next-line */
  }, [reports, filterLocation, filterSeverity, filterAffected, filterRepair, filterDateFrom, filterDateTo, search, sortBy, week]);

  // ----------------------------------------------------------------
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-gray-600">{t('auto.misc.damageIncidentReportsFor')} <strong>{projectName}</strong></div>
        {can('damage_reports.create') && (
          <Button size="sm" onClick={openNew} data-testid="project-new-damage-report">
            <Plus className="h-4 w-4 mr-1" /> {t('auto.misc.newDamageReport')}
          </Button>
        )}
      </div>

      <SearchSortBar
        search={search}
        onSearch={setSearch}
        sortBy={sortBy}
        onSort={setSortBy}
        placeholder={t('auto.misc.searchByReportNumberTitleDescription')}
        testIdPrefix="project-damage"
        week={week}
        onWeek={setWeek}
        options={[
          ...CREATED_SORT_OPTIONS,
          { value: 'number', label: t('auto.lbl.reportNumber') },
          { value: 'severity', label: t('auto.lbl.severityWorstFirst') },
          { value: 'incident_date', label: t('auto.lbl.incidentDate') },
          { value: 'repair_status', label: t('auto.lbl.repairStatus') },
        ]}
      />

      <OfflineNotice
        isOnline={isOnline}
        cached={offlineList}
        what="damage reports"
        testId="damage-offline-notice"
      />

      {/* Filters — wrap onto two rows when narrow. Each filter is auto-hidden
          when no instance offers a value, so the toolbar stays compact for
          empty projects. */}
      <div className="border rounded-md p-2 bg-gray-50/50 flex items-end gap-2 flex-wrap">
        {(locationOptions.length > 0 || filterLocation) && (
          <div className="flex flex-col gap-0.5">
            <Label className="text-[10px] uppercase text-gray-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{t('auto.misc.location')}</Label>
            <Select value={filterLocation || '__all__'} onValueChange={(v) => setFilterLocation(v === '__all__' ? '' : v)}>
              <SelectTrigger className="h-8 w-44" data-testid="damage-location-filter"><SelectValue placeholder={t('auto.misc.all')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t('auto.misc.allLocations')}</SelectItem>
                {locationOptions.map((loc) => <SelectItem key={loc} value={loc}>{loc}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex flex-col gap-0.5">
          <Label className="text-[10px] uppercase text-gray-500">{t('auto.misc.affected')}</Label>
          <Select value={filterAffected || '__all__'} onValueChange={(v) => setFilterAffected(v === '__all__' ? '' : v)}>
            <SelectTrigger className="h-8 w-36" data-testid="damage-affected-filter"><SelectValue placeholder={t('auto.misc.all')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{t('auto.misc.all')}</SelectItem>
              {affectedOptions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-0.5">
          <Label className="text-[10px] uppercase text-gray-500">{t('auto.misc.severity')}</Label>
          <Select value={filterSeverity || '__all__'} onValueChange={(v) => setFilterSeverity(v === '__all__' ? '' : v)}>
            <SelectTrigger className="h-8 w-32" data-testid="damage-severity-filter"><SelectValue placeholder={t('auto.misc.all')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{t('auto.misc.all')}</SelectItem>
              {SEVERITY_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-0.5">
          <Label className="text-[10px] uppercase text-gray-500">{t('auto.misc.repairStatus')}</Label>
          <Select value={filterRepair || '__all__'} onValueChange={(v) => setFilterRepair(v === '__all__' ? '' : v)}>
            <SelectTrigger className="h-8 w-36" data-testid="damage-repair-filter"><SelectValue placeholder={t('auto.misc.all')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{t('auto.misc.all')}</SelectItem>
              {REPAIR_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-0.5">
          <Label className="text-[10px] uppercase text-gray-500">{t('auto.misc.incidentFrom')}</Label>
          <Input type="date" className="h-8 w-36" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} data-testid="damage-date-from" />
        </div>
        <div className="flex flex-col gap-0.5">
          <Label className="text-[10px] uppercase text-gray-500">{t('auto.misc.incidentTo')}</Label>
          <Input type="date" className="h-8 w-36" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} data-testid="damage-date-to" />
        </div>
        {(filterLocation || filterSeverity || filterAffected || filterRepair || filterDateFrom || filterDateTo) && (
          <Button size="sm" variant="ghost" className="h-8" onClick={() => {
            setFilterLocation(''); setFilterSeverity(''); setFilterAffected('');
            setFilterRepair(''); setFilterDateFrom(''); setFilterDateTo('');
          }}>
            <X className="h-3.5 w-3.5 mr-1" /> {t('auto.misc.clearFilters')}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-400 text-sm">{t('auto.misc.loading')}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-sm">
          {reports.length === 0 ? t('auto.misc.noDamageReportsForThisProject') : t('auto.misc.noDamageReportsMatchTheCurrent')}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => {
            const totalSO = (r.sign_offs || []).length;
            const signedSO = (r.sign_offs || []).filter((so) => so.signed).length;
            const isLocked = r.status === 'signed_off';
            const canDelete = (user?.role === 'admin') || (!isLocked && r.created_by === user?.id) || isManager;
            return (
              <div
                key={r.id}
                className="border rounded-lg p-2.5 flex flex-col sm:flex-row sm:items-start sm:justify-between hover:bg-gray-50 hover:border-gray-300 cursor-pointer gap-1.5 sm:gap-2 transition-colors"
                onClick={() => openEdit(r)}
                data-testid={`damage-report-card-${r.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-1.5 sm:gap-3 min-w-0 flex-1">
                  <span className="inline-flex w-fit items-center gap-1 text-xs px-2 py-0.5 rounded border bg-red-50 text-red-700 border-red-200 flex-shrink-0 sm:mt-0.5">
                    <AlertTriangle className="h-3 w-3" /> {t('auto.misc.damage')}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-semibold">{r.report_number}</span>
                      {isLocalId(r.id) && <PendingSyncBadge testId={`damage-pending-${r.id}`} />}
                      <Badge variant="outline" className={`text-xs font-normal ${SEVERITY_COLORS[r.severity] || ''}`}>
                        {r.severity}
                      </Badge>
                      <Badge className={`${STATUS_COLORS[r.status] || STATUS_COLORS.draft} text-xs font-normal`}>
                        {(r.status || 'draft').replace('_', ' ')}
                      </Badge>
                      {isLocked && (
                        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">{t('auto.misc.locked')}</Badge>
                      )}
                      {r.affected_party && <span className="text-xs text-gray-500">{r.affected_party}</span>}
                    </div>
                    <div className="text-sm font-medium break-words mt-0.5">{r.title}</div>
                    {r.location && (
                      <div className="text-xs text-[#AA0000] font-medium mt-1 flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> <span className="truncate">{r.location}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-3 mt-1 flex-wrap text-xs text-gray-500">
                      {totalSO > 0 && (
                        <span className="flex items-center gap-1">
                          <FileSignature className="h-3 w-3" />
                          {signedSO === totalSO ? <span className="text-green-600">{t('auto.misc.signed')}</span> : <span>{signedSO}/{totalSO}</span>}
                        </span>
                      )}
                      {r.photo_count > 0 && (
                        <span className="flex items-center gap-1" title={t('auto.misc.photos')}>
                          <Camera className="h-3 w-3" /> {r.photo_count}
                        </span>
                      )}
                      {r.incident_date && (
                        <span title="Incident date"><span className="text-gray-400">On</span> {r.incident_date}{r.incident_time ? ` ${r.incident_time}` : ''}</span>
                      )}
                      {r.created_by_name && r.created_by_name !== '—' && (
                        <span title={t('auto.misc.reportedBy')}><span className="text-gray-400">by</span> {r.created_by_name}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-1 flex-shrink-0 border-t pt-1 sm:border-0 sm:pt-0" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" onClick={() => copyReport(r)} title="Copy as new draft" data-testid={`damage-copy-${r.id}`}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" title={t('auto.misc.exportPdf')}>
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <div className="px-2 py-1"><DocLangToggle testId="doc-lang-projectdamagereports-0" /></div>
                      <DropdownMenuItem onClick={() => exportPdf(r, false, false)}>
                        <Download className="h-4 w-4 mr-2" /> {t('auto.misc.colorPdf')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => exportPdf(r, true, false)}>
                        <Printer className="h-4 w-4 mr-2" /> Print-friendly (B&amp;W)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => exportPdf(r, false, true)}>
                        <Camera className="h-4 w-4 mr-2" /> {t('auto.misc.colorPhotoEvidence')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => exportPdf(r, true, true)}>
                        <Camera className="h-4 w-4 mr-2" /> B&amp;W + Photo Evidence
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  {canDelete && (
                    <Button variant="ghost" size="sm" onClick={() => deleteReport(r)} title="Delete" data-testid={`damage-delete-${r.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ---- Edit / Create dialog ---- */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.report_number}` : 'New damage report'}</DialogTitle>
            <DialogDescription>{t('auto.misc.recordDamageIncidentDetailsPhotosSign')}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            <div className="md:col-span-2">
              <Label>{t('auto.misc.title')}</Label>
              <Input value={form.title} onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value  }))} placeholder="e.g. Forklift hit MCC-01 cabinet" data-testid="damage-title-input" />
            </div>
            <div>
              <Label>{t('auto.misc.location')}</Label>
              <Input value={form.location} onChange={(e) => setForm(prev => ({ ...prev, location: e.target.value  }))} placeholder="e.g. Cabinet MCC-01 / South wall" data-testid="damage-location-input" />
            </div>
            <div>
              <Label>{t('auto.misc.severity')}</Label>
              <Select value={form.severity} onValueChange={(v) => setForm(prev => ({ ...prev, severity: v  }))}>
                <SelectTrigger data-testid="damage-severity-input"><SelectValue /></SelectTrigger>
                <SelectContent>{SEVERITY_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('auto.misc.incidentDate')}</Label>
              <Input type="date" value={form.incident_date} onChange={(e) => setForm(prev => ({ ...prev, incident_date: e.target.value  }))} />
            </div>
            <div>
              <Label>{t('auto.misc.incidentTime')}</Label>
              <Input type="time" value={form.incident_time} onChange={(e) => setForm(prev => ({ ...prev, incident_time: e.target.value  }))} />
            </div>
            <div>
              <Label>{t('auto.misc.affected')}</Label>
              <Select value={form.affected_party || '__none__'} onValueChange={(v) => setForm(prev => ({ ...prev, affected_party: v === '__none__' ? '' : v  }))}>
                <SelectTrigger data-testid="damage-affected-input"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {affectedOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('auto.misc.reportedBy')}</Label>
              <Input value={form.reported_by} onChange={(e) => setForm(prev => ({ ...prev, reported_by: e.target.value  }))} placeholder={t('auto.misc.nameOfPersonReporting')} />
            </div>
            <div className="md:col-span-2">
              <Label>{t('auto.misc.description')}</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value  }))} placeholder={t('auto.misc.whatHappenedCauseAndConsequence')} />
            </div>
            <div className="md:col-span-2 border-t pt-3">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t('auto.misc.repair')}</div>
            </div>
            <div>
              <Label>{t('auto.misc.repairStatus')}</Label>
              <Select value={form.repair_status} onValueChange={(v) => setForm(prev => ({ ...prev, repair_status: v  }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{REPAIR_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('auto.misc.estRepairTimeHours')}</Label>
              <Input type="number" step="0.25" value={form.estimated_repair_hours} onChange={(e) => setForm(prev => ({ ...prev, estimated_repair_hours: e.target.value  }))} placeholder="e.g. 3.5" />
            </div>
            <div>
              <Label>{t('auto.misc.estCostOptional')}</Label>
              <Input type="number" step="0.01" value={form.estimated_cost} onChange={(e) => setForm(prev => ({ ...prev, estimated_cost: e.target.value  }))} placeholder="0.00" />
            </div>
            <div>
              <Label>{t('auto.misc.currency')}</Label>
              <Select value={form.cost_currency} onValueChange={(v) => setForm(prev => ({ ...prev, cost_currency: v  }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['PLN', 'EUR', 'GBP', 'USD'].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>{t('auto.misc.replacementPartNoOptional')}</Label>
              <Input value={form.replacement_part_number} onChange={(e) => setForm(prev => ({ ...prev, replacement_part_number: e.target.value  }))} placeholder="e.g. SCHN-NSYM-200CV" />
            </div>
            <div className="md:col-span-2">
              <Label>{t('auto.misc.repairNotes')}</Label>
              <Textarea rows={2} value={form.repair_notes} onChange={(e) => setForm(prev => ({ ...prev, repair_notes: e.target.value  }))} placeholder={t('auto.misc.anyAdditionalNotesAboutTheRepair')} />
            </div>

            {/* Photos — only available on saved reports (need an ID) */}
            {editing && (
              <div className="md:col-span-2 border-t pt-3 mt-2">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center justify-between">
                  <span>{t('auto.misc.photoEvidence')}</span>
                  <span className="text-gray-400 normal-case font-normal">{t('auto.misc.includedInPdfPhotoEvidenceExports')}</span>
                </div>
                <EntityPhotoGallery
                  entityType="project_damage_report"
                  entityId={editing.id}
                  canEdit={!editing.locked && editing.status !== 'signed_off'}
                  showCount
                  onChange={() => fetchReports()}
                />
              </div>
            )}

            {/* Linked repair tasks */}
            {editing && (
              <DamageReportRepairTasks
                report={editing}
                canEdit={editing.status !== 'signed_off' || isManager}
                onChanged={(updated) => { setEditing(updated); fetchReports(); }}
              />
            )}

            {/* Sign-off — inline section with per-role row, like Test Reports */}
            {editing && (
              <div className="md:col-span-2 border-t pt-3 mt-2">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center justify-between">
                  <span>{t('auto.misc.signOff')}</span>
                  {editing.status === 'signed_off' ? (
                    <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">{t('auto.misc.locked')}</Badge>
                  ) : isManager && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={finaliseReport} title="Lock without remaining sign-offs (e.g. customer absent)" data-testid="btn-finalisereport">
                      <Lock className="h-3.5 w-3.5 mr-1" /> {t('auto.misc.finaliseLock')}
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  {(editing.sign_offs || []).map((so) => (
                    <SignOffRow
                      key={so.role}
                      so={so}
                      defaultName={user?.full_name || user?.username || ''}
                      disabled={editing.status === 'signed_off' || !isManager}
                      onSign={(name) => signRole(so.role, name)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          <FloatingSaveButton
            onClick={saveForm}
            disabled={saving}
            label={saving ? 'Saving…' : (editing ? t('auto.misc.saveChanges') : t('auto.misc.createDraft'))}
            testId="damage-floating-save-btn"
          />
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="dialog-cancel-btn">{t('auto.misc.cancel')}</Button>
            <Button onClick={saveForm} disabled={saving} data-testid="damage-save-btn">
              {saving ? 'Saving…' : (editing ? t('auto.misc.saveChanges') : t('auto.misc.createDraft'))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---- Inline sign-off row ----
const SignOffRow = ({ so, defaultName, disabled, onSign }) => {
  const { t } = useTranslation();
  const [name, setName] = useState(so.name || defaultName);
  if (so.signed) {
    const when = so.signed_at ? new Date(so.signed_at).toLocaleString(dateLocale()) : '';
    return (
      <div className="flex items-center gap-2 text-sm border rounded-md p-2 bg-green-50/40 border-green-100">
        <Badge className="bg-green-100 text-green-700 text-[10px] flex-shrink-0">{t('auto.misc.signed2')}</Badge>
        {so.pending_sync && <PendingSyncBadge testId={`damage-sign-pending-${so.role.replace(/\s+/g, '-').toLowerCase()}`} label="Pending sync" />}
        <div className="flex-1 min-w-0">
          <div className="font-medium">{so.role}</div>
          <div className="text-xs text-gray-500 truncate">{so.name || '—'} · {when}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm border rounded-md p-2 bg-white">
      <div className="w-full sm:w-44 font-medium sm:truncate flex-shrink-0">{so.role}</div>
      <Input className="h-11 sm:h-8 text-base sm:text-sm w-full sm:flex-1" placeholder="Signer's name" value={name} onChange={(e) => setName(e.target.value)} disabled={disabled} data-testid={`damage-sign-name-${so.role.replace(/\s+/g, '-').toLowerCase()}`} />
      <Button size="sm" className="w-full sm:w-auto h-11 sm:h-8 text-base sm:text-sm" disabled={disabled || !name.trim()} onClick={() => onSign(name)} data-testid={`damage-sign-btn-${so.role.replace(/\s+/g, '-').toLowerCase()}`}>
        <FileSignature className="h-3.5 w-3.5 mr-1" /> {t('auto.misc.sign')}
      </Button>
    </div>
  );
};

export default ProjectDamageReports;
